import React, { useState, useEffect } from 'react';
import { Product, ProductFormInputs } from '../../types/admin';
import ImageUploader from './ImageUploader';
import Spinner from '../common/Spinner';
import styles from '../../styles/admin/Dashboard.module.css';
import { categoryOptions } from '../../utils/options';

interface ProductEditorProps {
  product?: Product | null; // If provided, we're editing; if not, we're creating
  onSave: (product: ProductFormInputs) => Promise<void>;
  onCancel: () => void;
}

const emptyProductForm = {
  id: undefined as string | undefined,
  name: '',
  description: '',
  price: '',
  imageUrls: [] as string[],
  imageUrl: '',
  category: '',
  stock: '',
  createdAt: new Date(),
  updatedAt: new Date(),
  featured: false,
};

export const ProductEditor: React.FC<ProductEditorProps> = ({
  product,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState(emptyProductForm);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with product data if editing
  useEffect(() => {
    if (product) {
      setFormData({
        id: product.id,
        name: product.name,
        description: product.description,
        price: String(product.price),
        imageUrl: product.imageUrl,
        imageUrls: product.imageUrls || [product.imageUrl], // Use imageUrls if available, otherwise use imageUrl
        category: product.category,
        stock: String(product.stock),
        createdAt: product.createdAt ? product.createdAt : new Date(),
        updatedAt: product.updatedAt ? product.updatedAt : new Date(),
        featured: product.featured || false,
      });
    } else {
      setFormData(emptyProductForm);
    }
  }, [product]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    const dataToSave: ProductFormInputs = {
      ...formData,
      price: parseFloat(formData.price) || 0,
      stock: parseInt(formData.stock, 10) || 0,
      updatedAt: new Date(),
    };

    // Validate that at least one image is present
    if (!dataToSave.imageUrls.length || !dataToSave.imageUrl) {
      setErrors({ image: 'At least one image is required' });
      setIsSubmitting(false);
      return;
    }

    try {
      // TODO: [FIRESTORE-INTEGRATION]
      // 1. Upload product image to Firebase Storage
      // 2. Get download URL
      // 3. Save product data with image URL to Firestore
      console.log('Form data:', dataToSave);
      await onSave(dataToSave);
    } catch (err: any) {
      setErrors({ save: err.message || 'Failed to save product' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImagesChange = (imageUrls: string[]) => {
    console.log('Images changed:', imageUrls);
    setFormData((prev) => ({
      ...prev,
      imageUrls: imageUrls,
      imageUrl: imageUrls[0] || '', // Set the first image as the main image
    }));
  };

  return (
    <>
      <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>

      {errors.save && <div className={styles.errorMessage}>{errors.save}</div>}

      <form onSubmit={handleSubmit} className={styles.productForm}>
        <div className={styles.formGroup}>
          <label htmlFor="name">Product Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter product name"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description *</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            placeholder="Enter product description"
            rows={4}
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="price">Price ($) *</label>
            <input
              type="text"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              required
              inputMode="decimal"
              placeholder="e.g. 19.99"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="stock">Stock *</label>
            <input
              type="text"
              id="stock"
              name="stock"
              value={formData.stock}
              onChange={handleChange}
              required
              inputMode="numeric"
              placeholder="e.g. 100"
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="category">Category *</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            className={styles.categorySelect}
          >
            <option value="" disabled>
              Select a category
            </option>
            {categoryOptions.map((category) => (
              <option key={category} value={category}>
                {category
                  .split(' ')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>
            Featured
            <input
              type="checkbox"
              name="featured"
              className={styles.featuredCheckbox}
              checked={formData.featured}
              onChange={handleChange}
            />
          </label>
        </div>

        <div className={styles.formGroup}>
          <label>Images *</label>
          <ImageUploader
            onImagesChange={handleImagesChange}
            existingImages={formData.imageUrls || []}
          />
          {errors.image && (
            <span className={styles.errorMessage}>{errors.image}</span>
          )}
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            onClick={onCancel}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner size="small" color="white" />
            ) : product ? (
              'Update Product'
            ) : (
              'Add Product'
            )}
          </button>
        </div>
      </form>
    </>
  );
};

export default ProductEditor;
